import React, { useEffect, useState, useMemo } from 'react';
import { pickDataProvider } from '../helper/pickDataProvider';
import { ResourceContext } from '../Resource';
import { useResource } from './useResource';
import { useDataProvider } from './useDataProvider';
import merge from 'lodash/merge';

export const useList = (options) => {
  const { resources } = React.useContext(ResourceContext);
  const dataProvider = useDataProvider();
  const [result, setResult] = useState({
    loaded: false,
    loading: false,
    error: null,
    data: [],
  });

  const {
    resource: resourceName,
    dataProvider: dataProviderName,
    pagination,
    filters,
    sorters,
    meta,
    skip,
    lazy = false,
  } = options;
  const resource = useResource({ resourceName });

  const pickedDataProvider = pickDataProvider(resourceName, dataProviderName, resources);
  if (!resource) {
    throw new Error(`Resource "${resourceName}" not found`);
  }
  const pickedMeta = {
    ...resource.meta,
    ...meta,
  };
  const { getList } = dataProvider(pickedDataProvider);

  const memoizedFilters = useMemo(() => filters, [JSON.stringify(filters)]);
  const memoizedSorters = useMemo(() => sorters, [JSON.stringify(sorters)]);
  const memoizedPagination = useMemo(() => pagination, [JSON.stringify(pagination)]);

  const memoizedParams = useMemo(() => {
    return {
      resource: resource.list || resource.name,
      pagination: memoizedPagination,
      filters: memoizedFilters,
      sorters: memoizedSorters,
      meta: pickedMeta,
    };
  }, [memoizedFilters, memoizedSorters, memoizedPagination]);

  const refetch = (fetchOptions) => {
    setResult({ ...result, loading: true });
    return getList(merge({ ...memoizedParams, meta: pickedMeta }, fetchOptions))
      .then((data) => {
        setResult({ ...result, loading: false, data });
        return data;
      })
      .catch((error) => {
        setResult({ ...result, loading: false, error });
        throw error;
      });
  };

  useEffect(() => {
    if (skip) {
      return;
    }
    if (!lazy && !result.loaded && !result.loading) {
      setResult({ ...result, loading: true });
      getList(memoizedParams)
        .then((data) => {
          setResult({ loaded: true, loading: false, data });
        })
        .catch((error) => {
          setResult({ loaded: false, loading: false, error });
        });
    }
  }, [lazy, skip, result]);

  useEffect(() => {
    if (result.loaded && !result.loading) {
      refetch();
    }
  }, [memoizedParams]);

  return {
    getList,
    pagination,
    filters,
    sorters,
    meta: pickedMeta,
    refetch,
    ...result,
  };
};
